import {IStore} from "modules/types/store";
import {createSelector} from "reselect";
import {getViewedEvent} from "modules/selectors/events";
import {getPlayersListForViewedEvent} from "modules/selectors/players";
import {defaultPlayers, LINEUP_SIZE} from "modules/constants";
import {filter, get, identity, keyBy, merge, size} from "lodash";
import {BoosterActionType, BoosterType, EventStatus, RequestState} from "modules/enums";
import {getViewedSwingId} from "modules/selectors/fantasy_tiles";
import {IBooster, IEvent} from "modules/actions";

export const getTeamState = ({team: {hasUnsavedChanges, requestState}}: IStore) => ({
	hasUnsavedChanges,
	requestState,
});

export const getSuccessTeamSaveFlag = ({team}: IStore) => team.showSuccessSaveModal;

export const getTeamsByEventID = ({team}: IStore) => team.byEvent;

export const getViewedTeam = createSelector(
	getTeamsByEventID,
	getViewedEvent,
	(teamsByEventID, event): (typeof teamsByEventID)[number] | null =>
		teamsByEventID[event?.id || Number.MAX_SAFE_INTEGER] || null
);

export const getOldViewedTeamLineup = createSelector(
	getViewedTeam,
	getPlayersListForViewedEvent,
	(team, players) => {
		const emptyLineup = new Array<null>(LINEUP_SIZE).fill(null);
		const playersByID = keyBy(players, "id");
		const lineup = team?.lineup || [];
		const bench = team?.bench || [];
		const newLineup = [...lineup, ...bench];

		return merge(emptyLineup, newLineup).map((playerID) => playersByID[playerID!] || null);
	}
);

export const getViewedTeamLineup = createSelector(getViewedTeam, (team) => {
	if (!team) return defaultPlayers;

	return team?.players || [];
});

export const getPickedPlayersSize = createSelector(getViewedTeam, (team) =>
	size(filter(team?.players, ({playerID}) => identity(playerID)))
);

export const getIsTeamFull = createSelector(
	getPickedPlayersSize,
	(pickedPlayersSize) => pickedPlayersSize === LINEUP_SIZE
);

export const getModalPlayerID = ({team}: IStore) => team.showDetailsForPlayerID;
export const getLastPlayerIDCard = ({team}: IStore) => team.lastPlayerIDCard;
export const getIsTeamLoading = createSelector(
	getTeamState,
	(state) => state.requestState === RequestState.Requested
);

export const getTeamCaptainId = createSelector(getViewedTeam, (team) => team?.captain);

export const getIsCaptain = createSelector(getViewedTeam, (team) => (playerID?: number) => {
	if (!playerID) return false;

	return team?.captain === playerID;
});

export const getIsLineupPlayer = createSelector(getViewedTeam, (team) => (playerID: number) => {
	const index = get(team, "players", []).findIndex((player) => playerID === player.playerID);

	return index <= 3 && index !== -1;
});

export const getIsBenchPlayer = createSelector(
	getViewedTeam,
	(team) => (playerID: number) =>
		get(team, "players", []).findIndex((player) => playerID === player.playerID) > 3
);

export const getTeamBoosters = createSelector(getViewedTeam, (team) => get(team, "boosters", []));

export const getBoosterIsLoading = ({team}: IStore) =>
	team.boosterRequestState === RequestState.Requested;

const boosters = [
	{
		title: "TRIPLE CAPTAIN",
		description: "Your captain will earn triple points!",
		type: BoosterType.TRIPLE_CAPTAIN,
	},
	{
		title: "BENCH BOOST",
		description: "The 2 players on your bench will also earn points!",
		type: BoosterType.BENCH_BOOST,
	},
];

const getBoosterActionType = (boosterType: BoosterType, event: IEvent, boosters: IBooster[]) => {
	const eventBooster = boosters.find((booster) => booster.eventId === event.id);
	const isBoosterUsed = boosters.some((booster) => booster.type === boosterType);
	const isEventStarted = event?.status !== EventStatus.Scheduled;

	// if event is started
	if (isEventStarted) {
		// if booster is used
		if (isBoosterUsed) {
			return {
				type: BoosterActionType.USED,
				isDisabled: true,
			};
		}

		// if booster is not used
		return {
			type: BoosterActionType.APPLY,
			isDisabled: true,
		};
	}

	// if event is not started

	// if booster is used in swing
	if (isBoosterUsed && eventBooster?.type !== boosterType) {
		return {
			type: BoosterActionType.USED,
			isDisabled: true,
		};
	}

	// if booster is used in event
	if (eventBooster) {
		if (eventBooster.type === boosterType) {
			return {
				type: BoosterActionType.REMOVE,
				isDisabled: false,
			};
		}

		return {
			type: BoosterActionType.APPLY,
			isDisabled: true,
		};
	}

	return {
		type: BoosterActionType.APPLY,
		isDisabled: false,
	};
};

export const getBoostersList = createSelector(
	getTeamBoosters,
	getViewedSwingId,
	getBoosterIsLoading,
	getViewedEvent,
	(teamBoosters, swingId, isLoading, event) => {
		const swingBoosters = teamBoosters.filter((booster) => booster.swingId === swingId);

		return boosters.map((booster) => {
			const teamBooster = teamBoosters.find(({type}) => booster.type === type);

			return {
				...booster,
				action: getBoosterActionType(booster.type, event!, swingBoosters),
				id: teamBooster?.id,
				isLoading,
			};
		});
	}
);

export const getIsTripleCaptainActive = createSelector(
	getTeamBoosters,
	getViewedEvent,
	(boosters, event) =>
		boosters.some(
			(booster) =>
				event?.id === booster.eventId && booster.type === BoosterType.TRIPLE_CAPTAIN
		)
);

export const getIsBenchBoostActive = createSelector(
	getTeamBoosters,
	getViewedEvent,
	(boosters, event) =>
		boosters.some(
			(booster) => event?.id === booster.eventId && booster.type === BoosterType.BENCH_BOOST
		)
);

export const getIsTeamCreated = createSelector(getViewedTeam, (team) =>
	Boolean(team?.isTeamCreated)
);
